import { Link } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useGameStore } from '../store/gameStore'
import type { GuestBookEntry, GuestBookTag, TeahouseMood } from '../types'
import Icon, { type IconName } from '../components/Common/Icon'

/** 正文字数上限 (留言板时期 500, 茶馆放宽到 2000: 持股体验不该被截断) */
const MAX_LENGTH = 2000

/**
 * 话题标签元信息。
 * note/holding/afterhours/rant 是现用四话题; checkin/question 是留言板时期旧标签,
 * 仅用于渲染老条目与筛选计数, 不出现在写作区选择器。
 */
const TAG_META: Record<GuestBookTag, { label: string; icon: IconName; color: string }> = {
  note: { label: '心得', icon: 'note', color: 'var(--color-gold)' },
  holding: { label: '持股笔记', icon: 'card', color: 'var(--color-cyan)' },
  afterhours: { label: '盘后随想', icon: 'sparkle', color: 'var(--color-bull)' },
  rant: { label: '吐槽', icon: 'flame', color: 'var(--color-bear)' },
  checkin: { label: '打卡', icon: 'check', color: 'var(--color-bull)' },
  question: { label: '提问', icon: 'question', color: 'var(--color-cyan)' },
}

/** 写作区可选话题 (新四话题) */
const WRITABLE_TAGS: GuestBookTag[] = ['note', 'holding', 'afterhours', 'rant']

/** 全部话题 (筛选区, 含旧标签) */
const ALL_TAGS: GuestBookTag[] = ['note', 'holding', 'afterhours', 'rant', 'checkin', 'question']

/** 市况标记: Z 哥红黄灰择时框架, 给未来的自己留当天盘面上下文 */
const MOOD_META: Record<TeahouseMood, { label: string; color: string }> = {
  red: { label: '红市', color: 'var(--color-bull)' },
  yellow: { label: '黄市', color: 'var(--color-warning)' },
  grey: { label: '灰市', color: 'var(--color-text-dim)' },
}

const MOOD_ORDER: TeahouseMood[] = ['red', 'yellow', 'grey']

/** 本店店规: 四不原则 (Ch1 之魂, 挂墙上既是氛围也是复习) */
const HOUSE_RULES = ['不追', '不动', '不慌', '不乱摸']

/** 相对时间显示 */
function formatTime(ts: number): string {
  const diff = Date.now() - ts
  if (diff < 60_000) return '刚刚'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小时前`
  return `${Math.floor(diff / 86_400_000)} 天前`
}

/** 绝对时间 (悬停时显示完整日期) */
function formatFullTime(ts: number): string {
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * 茶馆页面 (留言板升级而来)
 * - 训练场的后院: 交易心得 / 持股体验 / 盘后随想, 想写什么写什么
 * - 纯本地存储 (IndexedDB), 不上传服务器
 * - 列表按时间倒序, 支持按话题筛选, 两段确认删除
 */
function TeahousePage() {
  const guestBookEntries = useGameStore(s => s.guestBookEntries)
  const addGuestBookEntry = useGameStore(s => s.addGuestBookEntry)
  const removeGuestBookEntry = useGameStore(s => s.removeGuestBookEntry)
  const isInitialized = useGameStore(s => s.isInitialized)
  const initialize = useGameStore(s => s.initialize)

  // 写作区状态
  const [content, setContent] = useState('')
  const [tag, setTag] = useState<GuestBookTag>('note')
  const [mood, setMood] = useState<TeahouseMood | null>(null)
  // 刚发布的高亮提示
  const [justPosted, setJustPosted] = useState(false)
  // 列表话题筛选 (null = 全部)
  const [filterTag, setFilterTag] = useState<GuestBookTag | null>(null)
  // 删除确认: 记录处于「待确认」状态的条目 id
  const [confirmingId, setConfirmingId] = useState<string | null>(null)

  // 直接访问 /teahouse 时兜底初始化 (与首页同一套逻辑, 幂等)
  useEffect(() => {
    if (!isInitialized) {
      initialize()
    }
  }, [isInitialized, initialize])

  const charCount = content.length
  const overLimit = charCount > MAX_LENGTH
  const canPost = content.trim().length > 0 && !overLimit

  // 各话题计数 (筛选 chips 上的数字, 含旧标签条目)
  const tagCounts = useMemo(() => {
    const counts: Record<GuestBookTag, number> = {
      note: 0, checkin: 0, question: 0, rant: 0, holding: 0, afterhours: 0,
    }
    for (const e of guestBookEntries) counts[e.tag]++
    return counts
  }, [guestBookEntries])

  const visibleEntries = useMemo(
    () => (filterTag ? guestBookEntries.filter(e => e.tag === filterTag) : guestBookEntries),
    [guestBookEntries, filterTag]
  )

  const handlePost = () => {
    if (!canPost) return
    addGuestBookEntry(content, tag, mood ?? undefined)
    setContent('')
    setMood(null)
    setJustPosted(true)
    window.setTimeout(() => setJustPosted(false), 2000)
  }

  const handleDelete = (id: string) => {
    if (confirmingId === id) {
      removeGuestBookEntry(id)
      setConfirmingId(null)
    } else {
      setConfirmingId(id)
      // 3 秒不确认自动复位, 防误触
      window.setTimeout(() => {
        setConfirmingId(cur => (cur === id ? null : cur))
      }, 3000)
    }
  }

  return (
    <div className="min-h-screen p-4 md:p-6 pb-16 crt-scanlines">
      <div className="max-w-2xl mx-auto">
        <Link
          to="/"
          className="font-pixel-display text-[10px] text-[color:var(--color-cyan)] hover:text-[color:var(--color-bull)] mb-3 inline-block"
        >
          ◀ 返回首页
        </Link>

        {/* ============ 标题区 ============ */}
        <div className="pixel-box pixel-box-bright p-4 mb-4 text-center">
          <div className="font-pixel text-[10px] text-[color:var(--color-gold)] mb-2 tracking-wider pixel-blink">
            ► 茶馆
          </div>
          <h1 className="font-pixel text-lg md:text-xl text-[color:var(--color-text-bright)] mb-1 inline-flex items-center justify-center gap-2">
            <Icon name="teacup" size={20} className="text-[color:var(--color-gold)]" />
            茶馆
          </h1>
          <p className="font-pixel-body text-sm text-[color:var(--color-text-dim)] mt-1">
            茶已沏好，随便坐 — 交易心得 · 持股体验 · 想写什么写什么 · 共 {guestBookEntries.length} 条
          </p>
          <p className="font-pixel-display text-[9px] text-[color:var(--color-text-dim)] mt-2 inline-flex items-center gap-1.5">
            <Icon name="lock" size={10} />
            茶客留的话只存在你的浏览器里, 绝不上传服务器
          </p>
        </div>

        {/* ============ 店规墙 (四不原则) ============ */}
        <div className="pixel-box p-3 mb-4 border-[color:var(--color-gold)] bg-[color:var(--color-bg-elevated)]">
          <div className="font-pixel text-[9px] text-[color:var(--color-gold)] mb-2 tracking-wider">
            ► 本店店规 · 四不原则
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            {HOUSE_RULES.map(rule => (
              <div
                key={rule}
                className="pixel-box py-2 px-1 font-pixel text-[10px] text-[color:var(--color-text-bright)]"
              >
                {rule}
              </div>
            ))}
          </div>
        </div>

        {/* ============ 说书台 (答案之书入口) ============ */}
        <Link
          to="/oracle"
          className="block pixel-box p-3 mb-4 border-[color:var(--color-cyan)] bg-[color:var(--color-bg-elevated)] hover:bg-[color:var(--color-bg-base)] transition-colors group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Icon name="scroll" size={15} className="text-[color:var(--color-cyan)]" />
              <div>
                <div className="font-pixel text-[10px] text-[color:var(--color-cyan)]">
                  说书台
                </div>
                <div className="font-pixel-body text-xs text-[color:var(--color-text-dim)] mt-0.5">
                  Z 哥金句 · 抽一签听听
                </div>
              </div>
            </div>
            <span className="font-pixel text-sm text-[color:var(--color-cyan)] group-hover:translate-x-1 transition-transform">
              ▶
            </span>
          </div>
        </Link>

        {/* ============ 写作区 ============ */}
        <div className="pixel-box p-4 mb-4 bg-[color:var(--color-bg-elevated)]">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-pixel text-[10px] text-[color:var(--color-cyan)] tracking-wider inline-flex items-center gap-1.5">
              <Icon name="note" size={11} />
              沏一壶，记一笔
            </h2>
            {justPosted && (
              <span className="pixel-tag text-[color:var(--color-bull)] border-[color:var(--color-bull)] pixel-pop inline-flex items-center gap-1">
                <Icon name="check" size={10} />
                已上墙
              </span>
            )}
          </div>

          {/* 话题选择 */}
          <div className="flex flex-wrap gap-2 mb-3">
            {WRITABLE_TAGS.map(t => {
              const meta = TAG_META[t]
              const active = tag === t
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTag(t)}
                  className="pixel-button text-[10px] transition-transform"
                  style={
                    active
                      ? {
                          borderColor: meta.color,
                          color: meta.color,
                          transform: 'translate(-1px, -1px)',
                          boxShadow: `0 0 8px ${meta.color}`,
                        }
                      : { opacity: 0.75 }
                  }
                  title={active ? '当前话题' : `选择话题: ${meta.label}`}
                >
                  <span className="inline-flex items-center gap-1">
                    <Icon name={meta.icon} size={11} />
                    {meta.label}
                  </span>
                </button>
              )
            })}
          </div>

          {/* 市况标记 (可选) */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="font-pixel text-[9px] text-[color:var(--color-text-dim)]">
              今日市况:
            </span>
            {MOOD_ORDER.map(m => {
              const meta = MOOD_META[m]
              const active = mood === m
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMood(active ? null : m)}
                  className="pixel-tag cursor-pointer inline-flex items-center gap-1.5 transition-transform hover:scale-105"
                  style={
                    active
                      ? { color: meta.color, borderColor: meta.color, boxShadow: `0 0 6px ${meta.color}` }
                      : { color: 'var(--color-text-dim)', borderColor: 'var(--color-border)', opacity: 0.7 }
                  }
                  title={active ? '再点一下取消' : `标记市况: ${meta.label}`}
                >
                  <span
                    className="inline-block w-1.5 h-1.5"
                    style={{ backgroundColor: meta.color }}
                  />
                  {meta.label}
                </button>
              )
            })}
            <span className="font-pixel-display text-[8px] text-[color:var(--color-text-dim)]">
              (可选)
            </span>
          </div>

          {/* 正文输入 */}
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="今天买了什么？为什么买？拿着什么感觉？踩了什么坑、悟到了什么？写下来，未来的你会感谢今天的你……"
            rows={5}
            className="w-full p-3 font-pixel-body text-base leading-relaxed resize-y
              bg-[color:var(--color-bg-base)] text-[color:var(--color-text-bright)]
              border-3 border-[color:var(--color-border)] focus:border-[color:var(--color-border-bright)]
              focus:outline-none transition-colors placeholder:text-[color:var(--color-text-dim)]"
          />

          {/* 字数统计 + 发布 */}
          <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
            <span
              className={`font-pixel-display text-[10px] ${
                overLimit
                  ? 'text-[color:var(--color-bear)] pixel-blink'
                  : charCount > MAX_LENGTH - 100
                    ? 'text-[color:var(--color-warning)]'
                    : 'text-[color:var(--color-text-dim)]'
              }`}
            >
              {charCount}/{MAX_LENGTH}
              {overLimit && ' 字'}
            </span>
            <button
              type="button"
              onClick={handlePost}
              disabled={!canPost}
              className={`pixel-button pixel-button-primary text-[10px] ${
                canPost ? '' : 'opacity-40 cursor-not-allowed'
              }`}
            >
              ▶ 上墙
            </button>
          </div>
          {overLimit && (
            <p className="font-pixel-body text-sm text-[color:var(--color-bear)] mt-1 inline-flex items-center gap-1.5">
              <Icon name="warn" size={12} />
              超出 {charCount - MAX_LENGTH} 字，删减一点再上墙
            </p>
          )}
        </div>

        {/* ============ 话题筛选 ============ */}
        {guestBookEntries.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button
              type="button"
              onClick={() => setFilterTag(null)}
              className="pixel-tag cursor-pointer transition-transform hover:scale-105"
              style={
                filterTag === null
                  ? { color: 'var(--color-text-bright)', borderColor: 'var(--color-text-bright)' }
                  : { color: 'var(--color-text-dim)', borderColor: 'var(--color-text-dim)', opacity: 0.7 }
              }
            >
              全部 {guestBookEntries.length}
            </button>
            {ALL_TAGS.filter(t => tagCounts[t] > 0).map(t => {
              const meta = TAG_META[t]
              const active = filterTag === t
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setFilterTag(active ? null : t)}
                  className="pixel-tag cursor-pointer transition-transform hover:scale-105"
                  style={{
                    color: meta.color,
                    borderColor: meta.color,
                    opacity: active ? 1 : 0.6,
                    boxShadow: active ? `0 0 6px ${meta.color}` : 'none',
                  }}
                >
                  <span className="inline-flex items-center gap-1">
                    <Icon name={meta.icon} size={10} />
                    {meta.label} {tagCounts[t]}
                  </span>
                </button>
              )
            })}
          </div>
        )}

        {/* ============ 留言墙 ============ */}
        {guestBookEntries.length === 0 ? (
          <div className="pixel-box p-6 text-center">
            <div className="mb-3 text-[color:var(--color-text-dim)]">
              <Icon name="teacup" size={28} />
            </div>
            <p className="font-pixel text-[10px] text-[color:var(--color-text-bright)] mb-2">
              茶馆刚开张，还没有茶客留话
            </p>
            <p className="font-pixel-body text-sm text-[color:var(--color-text-dim)]">
              写下第一条吧：一笔交易的心得，或者今天持股的心情。
            </p>
          </div>
        ) : visibleEntries.length === 0 ? (
          <div className="pixel-box p-6 text-center">
            <p className="font-pixel-body text-sm text-[color:var(--color-text-dim)]">
              ► 这个话题下还没有留话
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {visibleEntries.map((entry, idx) => (
              <EntryCard
                key={entry.id}
                entry={entry}
                index={idx}
                confirming={confirmingId === entry.id}
                onDelete={() => handleDelete(entry.id)}
              />
            ))}
          </div>
        )}

        {/* 底部说明 */}
        <div className="mt-4 text-center font-pixel-display text-[10px] text-[color:var(--color-text-dim)]">
          ► 留话保存在浏览器 IndexedDB, 清除浏览器数据会一并删除
        </div>
      </div>
    </div>
  )
}

/** 单条留话卡片 (便签上墙) */
function EntryCard({
  entry,
  index,
  confirming,
  onDelete,
}: {
  entry: GuestBookEntry
  index: number
  confirming: boolean
  onDelete: () => void
}) {
  const meta = TAG_META[entry.tag]
  const moodMeta = entry.mood ? MOOD_META[entry.mood] : null
  return (
    <div
      className="pixel-box pixel-box-hover p-3 pixel-pop"
      style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
    >
      <div className="flex items-start gap-3">
        {/* 左侧话题色条 */}
        <div
          className="w-1 self-stretch flex-shrink-0"
          style={{ backgroundColor: meta.color }}
          aria-hidden
        />
        <div className="flex-1 min-w-0">
          {/* 话题徽章 + 市况徽章 + 时间 */}
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span
              className="pixel-tag inline-flex items-center gap-1"
              style={{ color: meta.color, borderColor: meta.color }}
            >
              <Icon name={meta.icon} size={10} />
              {meta.label}
            </span>
            {moodMeta && (
              <span
                className="pixel-tag inline-flex items-center gap-1"
                style={{ color: moodMeta.color, borderColor: moodMeta.color }}
                title="写这条时的市况"
              >
                <span
                  className="inline-block w-1.5 h-1.5"
                  style={{ backgroundColor: moodMeta.color }}
                />
                {moodMeta.label}
              </span>
            )}
            <span
              className="font-pixel-display text-[9px] text-[color:var(--color-text-dim)]"
              title={formatFullTime(entry.createdAt)}
            >
              {formatTime(entry.createdAt)}
            </span>
          </div>
          {/* 正文 (保留换行) */}
          <p className="font-pixel-body text-base text-[color:var(--color-text-bright)] whitespace-pre-wrap break-words leading-relaxed">
            {entry.content}
          </p>
        </div>
        {/* 删除按钮 (两段确认) */}
        <button
          type="button"
          onClick={onDelete}
          className={`pixel-button text-[9px] flex-shrink-0 ${
            confirming ? 'pixel-button-warn' : ''
          }`}
          style={confirming ? undefined : { opacity: 0.55 }}
          title={confirming ? '再点一次确认删除' : '撕掉这条留话'}
        >
          {confirming ? (
            '确认?'
          ) : (
            <span className="inline-flex items-center gap-1">
              <Icon name="cross" size={9} />
              撕掉
            </span>
          )}
        </button>
      </div>
    </div>
  )
}

export default TeahousePage
